"use client"

import {
  expandAllFeature,
  hotkeysCoreFeature,
  searchFeature,
  selectionFeature,
  syncDataLoaderFeature,
  type TreeState,
} from "@headless-tree/core"
import { useTree } from "@headless-tree/react"
import { useDebounceFn } from "ahooks"
import { SearchIcon } from "lucide-react"
import Link from "next/link"
import { type ChangeEvent, useEffect, useMemo, useState } from "react"
import { Input } from "@/components/ui/input"
import { Tree, TreeItem, TreeItemLabel } from "@/components/ui/tree"
import type { StoreSidebarCategoryNode } from "@/services/dokan-service"

interface StoreCategoriesTreeProps {
  categories: StoreSidebarCategoryNode[]
}

interface SidebarTreeItem {
  count: number
  name: string
  slug?: string
  children?: string[]
}

const ROOT_ID = "__store-categories-root__"
const INDENT = 16
const SEARCH_DEBOUNCE_MS = 200

const createNodeId = (categoryId: number): string => `category-${categoryId}`

const buildTreeData = (
  categories: StoreSidebarCategoryNode[]
): {
  items: Record<string, SidebarTreeItem>
  initialExpandedItems: string[]
  parentById: Record<string, string>
} => {
  const items: Record<string, SidebarTreeItem> = {
    [ROOT_ID]: {
      count: 0,
      name: "Categorias",
      children: categories.map((category) => createNodeId(category.id)),
    },
  }
  const parentById: Record<string, string> = {}

  const initialExpandedItems = [ROOT_ID]

  const walkCategory = (
    category: StoreSidebarCategoryNode,
    parentId: string
  ): void => {
    const nodeId = createNodeId(category.id)
    const childIds = category.children.map((child) => createNodeId(child.id))
    parentById[nodeId] = parentId

    items[nodeId] = {
      count: category.count,
      name: category.name,
      slug: category.slug,
      ...(childIds.length > 0 ? { children: childIds } : {}),
    }

    for (const childCategory of category.children) {
      walkCategory(childCategory, nodeId)
    }
  }

  for (const category of categories) {
    walkCategory(category, ROOT_ID)
  }

  return {
    items,
    initialExpandedItems,
    parentById,
  }
}

export const StoreCategoriesTree = ({
  categories,
}: StoreCategoriesTreeProps): React.ReactNode => {
  const { items, initialExpandedItems, parentById } = useMemo(
    () => buildTreeData(categories),
    [categories]
  )
  const [state, setState] = useState<Partial<TreeState<SidebarTreeItem>>>({})
  const [searchValue, setSearchValue] = useState("")

  const tree = useTree<SidebarTreeItem>({
    dataLoader: {
      getChildren: (itemId) => items[itemId]?.children ?? [],
      getItem: (itemId) => items[itemId],
    },
    features: [
      syncDataLoaderFeature,
      hotkeysCoreFeature,
      selectionFeature,
      searchFeature,
      expandAllFeature,
    ],
    getItemName: (item) => item.getItemData().name,
    indent: INDENT,
    initialState: {
      expandedItems: initialExpandedItems,
    },
    isItemFolder: (item) => (item.getItemData()?.children?.length ?? 0) > 0,
    rootItemId: ROOT_ID,
    setState,
    state,
  })

  const searchInputProps = tree.getSearchInputElementProps()
  const { run: runSearchChange, cancel: cancelSearchChange } = useDebounceFn(
    (value: string) => {
      if (searchInputProps.onChange) {
        const nextEvent = {
          target: { value },
          currentTarget: { value },
        } as ChangeEvent<HTMLInputElement>
        searchInputProps.onChange(nextEvent)
      }

      const normalizedQuery = value.trim().toLocaleLowerCase()
      const hasSearch = normalizedQuery.length > 0

      if (!hasSearch) {
        setState((previousState) => ({
          ...previousState,
          expandedItems: initialExpandedItems,
        }))
        return
      }

      const expandedItems = new Set<string>(initialExpandedItems)

      for (const [itemId, item] of Object.entries(items)) {
        if (itemId === ROOT_ID) {
          continue
        }

        const isMatch = item.name.toLocaleLowerCase().includes(normalizedQuery)
        if (!isMatch) {
          continue
        }

        let currentId: string | undefined = itemId
        while (currentId) {
          expandedItems.add(currentId)

          if (currentId === ROOT_ID) {
            break
          }

          currentId = parentById[currentId]
        }
      }

      setState((previousState) => ({
        ...previousState,
        expandedItems: [...expandedItems],
      }))
    },
    { wait: SEARCH_DEBOUNCE_MS }
  )

  useEffect(() => {
    const inputValue =
      typeof searchInputProps.value === "string" ? searchInputProps.value : ""
    setSearchValue(inputValue)
  }, [searchInputProps.value])

  useEffect(() => {
    return () => {
      cancelSearchChange()
    }
  }, [cancelSearchChange])

  return (
    <>
      <div className='relative mt-4'>
        <Input
          className='peer ps-9'
          {...searchInputProps}
          onChange={(event) => {
            const value = event.target.value
            setSearchValue(value)
            runSearchChange(value)
          }}
          placeholder='Buscar categoria'
          type='search'
          value={searchValue}
        />
        <div className='pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-muted-foreground/80 peer-disabled:opacity-50'>
          <SearchIcon aria-hidden='true' className='size-4' />
        </div>
      </div>

      <Tree className='mt-3' indent={INDENT} tree={tree}>
        {tree.getItems().map((item) => {
          if (item.getId() === ROOT_ID) {
            return null
          }

          const count = item.getItemData().count
          const slug = item.getItemData().slug

          return (
            <TreeItem item={item} key={item.getId()}>
              <TreeItemLabel>
                <span className='flex min-w-0 flex-1 items-center gap-2'>
                  {slug ? (
                    <Link
                      className='truncate hover:text-primary hover:underline'
                      href={`/categorias/${slug}`}
                      onClick={(event) => {
                        event.stopPropagation()
                      }}
                      prefetch
                    >
                      {item.getItemName()}
                    </Link>
                  ) : (
                    <span className='truncate'>{item.getItemName()}</span>
                  )}
                  <span className='ms-auto text-gray-500 text-xs'>{count}</span>
                </span>
              </TreeItemLabel>
            </TreeItem>
          )
        })}
      </Tree>
    </>
  )
}
